import { matchRoute, type MatchResult } from './routes.js';
import type { AppContext, LeagueMembership } from './types.js';

export interface RouteDecision {
  allowed: boolean;
  routeId?: string;
  redirectTo?: string;
  reason?: string;
}

function resolveMembership(
  memberships: LeagueMembership[],
  leagueId: string | undefined,
): LeagueMembership | null {
  if (!leagueId) {
    return null;
  }

  return memberships.find((membership) => membership.leagueId === leagueId) ?? null;
}

function unauthorizedDecision(redirectTo: string, reason: string): RouteDecision {
  return {
    allowed: false,
    redirectTo,
    reason,
  };
}

function evaluateMatchedRoute(match: MatchResult, context: AppContext): RouteDecision {
  const { route, params } = match;

  if (route.access === 'public') {
    return { allowed: true, routeId: route.id };
  }

  if (!context.session) {
    return unauthorizedDecision('/', 'Authentication is required');
  }

  if (route.access === 'authenticated') {
    return { allowed: true, routeId: route.id };
  }

  const membership = resolveMembership(context.memberships, params.leagueId);
  if (!membership) {
    return unauthorizedDecision('/leagues', 'User is not a member of this league');
  }

  if (route.access === 'league-member') {
    return { allowed: true, routeId: route.id };
  }

  if (membership.role !== 'admin') {
    return unauthorizedDecision('/leagues', 'Admin role is required');
  }

  return { allowed: true, routeId: route.id };
}

export function evaluateRouteAccess(path: string, context: AppContext): RouteDecision {
  const matched = matchRoute(path);
  if (!matched) {
    return unauthorizedDecision(context.session ? '/leagues' : '/', 'Route not found');
  }

  return evaluateMatchedRoute(matched, context);
}
